import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Collapse,
  Container,
  ListGroup,
  Row,
} from "react-bootstrap";
import { toast } from "react-toastify";
import PastTermsTable from "../PastTermsTable";
import AcademicYearSelector from "../AcademicYearSelector";
import { useAcademicYear } from "../AcademicYearContext";
import api from "common/api";
import { Term } from "common/types";

const StudentDashboardLessons = () => {
  const [upcomingLessons, setUpcomingLessons] = useState<Term[]>([]);
  const [pastLessons, setPastLessons] = useState<Term[]>([]);
  const [expandedDetails, setExpandedDetails] = useState<{
    [key: number]: boolean;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const { selectedAcademicYear, getAcademicYear, getCurrentAcademicYear } =
    useAcademicYear();

  useEffect(() => {
    fetchUpcomingLessons();
    fetchPastLessons();
  }, []);

  const fetchUpcomingLessons = async () => {
    setLoading(true);
    try {
      const now = new Date();
      now.setHours(now.getHours() - 2);
      const twoHoursAgo = now.toISOString();

      const response = await api.get(
        `/calendars/?status=PAID&start_after=${encodeURIComponent(
          twoHoursAgo,
        )}`,
      );
      setUpcomingLessons(response.data);
    } catch (error) {
      console.error(error);
      toast.error("Greška prilikom dohvaćanja termina.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPastLessons = async () => {
    try {
      const response = await api.get(`/calendars/?status=PAID`);
      setPastLessons(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const toggleDetails = (index: number) => {
    setExpandedDetails((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Filter lessons by academic year
  const filterLessonsByAcademicYear = (lessons: Term[]): Term[] => {
    if (selectedAcademicYear === "all") {
      return lessons;
    }

    const targetYear =
      selectedAcademicYear === "current"
        ? getCurrentAcademicYear()
        : selectedAcademicYear;

    return lessons.filter((lesson) => {
      const lessonAcademicYear = getAcademicYear(new Date(lesson.start));
      return lessonAcademicYear === targetYear;
    });
  };

  const filteredPastLessons = filterLessonsByAcademicYear(pastLessons);

  return (
    <Container>
      <AcademicYearSelector terms={pastLessons} />
      <Row>
        <Col>
          <h2>Nadolazeći termini poduka</h2>
          {loading ? (
            <p>Učitavanje...</p>
          ) : (
            <ListGroup>
              {upcomingLessons.length > 0 ? (
                upcomingLessons.map((lesson, index) => {
                  const subjectName = lesson.subject?.name || "Predmet";
                  return (
                    <ListGroup.Item key={lesson.id}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{subjectName}</strong> -{" "}
                          {new Date(lesson.start).toLocaleString("hr")} do{" "}
                          {new Date(lesson.end).toLocaleString("hr")}
                        </div>
                        <Button
                          variant="link"
                          onClick={() => toggleDetails(index)}
                          aria-controls={`lesson-details-${index}`}
                          aria-expanded={!!expandedDetails[index]}
                        >
                          {expandedDetails[index]
                            ? "Sakrij detalje"
                            : "Prikaži detalje"}
                        </Button>
                      </div>

                      <Collapse in={!!expandedDetails[index]}>
                        <div id={`lesson-details-${index}`}>
                          {lesson.meet_url && (
                            <div className="mt-2">
                              <strong>Video link: </strong>
                              <a
                                href={lesson.meet_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {lesson.meet_url}
                              </a>
                            </div>
                          )}

                          {lesson.remark && (
                            <div className="mt-2">
                              <strong>Napomena: </strong>
                              {lesson.remark}
                            </div>
                          )}

                          <div className="mt-2">
                            <strong>Nastavnik: </strong>
                            {lesson.teacher.first_name}{" "}
                            {lesson.teacher.last_name}
                          </div>

                          <div className="mt-2">
                            <strong>Kontakt email: </strong>
                            <a href={`mailto:${lesson.teacher?.email}`}>
                              {lesson.teacher?.email}
                            </a>
                          </div>

                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="mt-2"
                            href={`mailto:${lesson.teacher?.email}`}
                            as="a"
                          >
                            Pošalji email nastavniku
                          </Button>
                        </div>
                      </Collapse>
                    </ListGroup.Item>
                  );
                })
              ) : (
                <ListGroup.Item>Nema nadolazećih termina</ListGroup.Item>
              )}
            </ListGroup>
          )}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <PastTermsTable terms={filteredPastLessons} />
        </Col>
      </Row>
    </Container>
  );
};

export default StudentDashboardLessons;
